import React, { useCallback, useState, useEffect } from 'react'
import _ from 'underscore'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import Form from 'pages/shared/Form'
import FormSection from 'pages/shared/FormSection'
import BrandFields from 'pages/shared/FeatureTourForm/BrandFields'
import RoleFields from 'pages/shared/FeatureTourForm/RoleFields'
import TierFields from 'pages/shared/FeatureTourForm/TierFields'
import FeatureTourFields from 'pages/shared/FeatureTourForm/FeatureTourFields'
import FeatureTourModal from 'pages/shared/FeatureTourModal'

import ListSelectAndSort from 'components/ListSelectAndSort'
import ErrorMessage from 'components/ErrorMessage'
import LegacyButton from 'components/LegacyButton'

import Api from 'services/api'
import FeatureTour from 'models/FeatureTour'
import { color } from 'styles/colors'
import { useConfirmDialog, useAlertDialog } from 'hooks/useDialog'
import { CONFIRMATION_DIALOG_MESSAGES } from 'helpers/confirmationDialogMessages'

import { SiteMessageDetail } from 'types/SiteMessageDetail'

type SiteMessageCard = SiteMessageDetail & {
  sort_position: number
  published: boolean
  tiers: string[]
  tag: string
}

type FeatureTourAttributes = {
  id: number | null
  internalName: string
  title: string
  message: string
  brands: string[]
  roles: string[]
  tiers: string[]
  published: boolean
  publishedAt: string | null
  expired: boolean
  expiredAt: string | null
  siteMessages: SiteMessageCard[]
  isUpdatedCards: boolean
}

type FeatureTourModel = FeatureTourAttributes & {
  save: (attrs: FeatureTourAttributes, onSuccess: () => void, onFail: () => void, onValidationError: (e: FormErrors) => void) => void
  delete: (onSuccess: () => void, onFail: () => void) => void
}

type FormErrors = { internalName?: string; title?: string; message?: string; brands?: string; roles?: string; tiers?: string; siteMessages?: string }

type FeatureTourFormProps = {
  featureTour?: FeatureTourModel
}

const IFeatureTour = new FeatureTour() as unknown as FeatureTourModel

const FeatureTourForm = ({ featureTour = IFeatureTour }: FeatureTourFormProps) => {
  const navigate = useNavigate()

  const [selectedSiteMessages, setSelectedSiteMessages] = useState<SiteMessageCard[]>([])
  const [availableSiteMessages, setAvailableSiteMessages] = useState<SiteMessageCard[]>([])
  const [featureTourAttributes, setFeatureTourAttributes] = useState<FeatureTourAttributes>({ ...featureTour })
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [openPreview, setOpenPreview] = useState(false)
  const { confirm } = useConfirmDialog()
  const { alert } = useAlertDialog()
  const componentType = 'Feature Tour'

  const SiteMessageList = (msgs: SiteMessageCard[]): SiteMessageCard[] =>
    msgs.map((m, index) => ({
      ...m,
      id: String(m.id || ''),
      messageType: m.messageType || 'info',
      publishedAt: m.publishedAt || '',
      image: m.image || '',
      imageType: m.imageType || 'image',
      actionButtonCaption: m.actionButtonCaption || '',
      actionButtonLink: m.actionButtonLink || '',
      sort_position: index,
      tag: !m.published ? 'draft' : ''
    }))

  useEffect(() => {
    setSelectedSiteMessages(SiteMessageList(featureTour.siteMessages))
  }, [featureTour])

  useEffect(() => {
    Api.SiteMessages.availableSiteMessages(
      String(featureTour.id || ''),
      featureTourAttributes.brands.join(','),
      featureTourAttributes.roles.join(','),
      featureTourAttributes.tiers.join(',')
    )
      .then((res: SiteMessageCard[]) => {
        setAvailableSiteMessages(res)
        setSelectedSiteMessages(SiteMessageList(featureTour.siteMessages))
      })
      .catch(() => {
        setAvailableSiteMessages([])
      })
  }, [featureTourAttributes.brands, featureTourAttributes.roles, featureTourAttributes.tiers]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSaveCallback = useCallback(() => {
    setIsSaving(true)
    featureTour.save(
      featureTourAttributes,
      () => navigate('/admin/feature_tours'),
      () => { setIsSaving(false); alert({ title: CONFIRMATION_DIALOG_MESSAGES.ERROR_MESSAGE.SAVE_FAILED, description: '' }) },
      (errs: FormErrors) => { setErrors(errs); setIsSaving(false) }
    )
  }, [navigate, featureTour, featureTourAttributes]) // eslint-disable-line react-hooks/exhaustive-deps

  const onCancelCallback = useCallback(async () => {
    const hasChanges = !_.isEqual(featureTourAttributes, { ...featureTour })
    const isConfirmed = await confirm({ title: CONFIRMATION_DIALOG_MESSAGES.CANCEL.TITLE, description: CONFIRMATION_DIALOG_MESSAGES.CANCEL.DESCRIPTION, confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.OK })
    if (!hasChanges || isConfirmed) navigate('/admin/feature_tours')
  }, [navigate, featureTour, featureTourAttributes]) // eslint-disable-line react-hooks/exhaustive-deps

  const onFeatureTourUpdateCallback = useCallback((updatedAttributes: Partial<FeatureTourAttributes>) => {
    setFeatureTourAttributes({ ...featureTourAttributes, ...updatedAttributes })
  }, [featureTourAttributes])

  const onDeleteCallback = useCallback(async () => {
    const isConfirmed = await confirm({ title: CONFIRMATION_DIALOG_MESSAGES.DELETE.TITLE(componentType), description: CONFIRMATION_DIALOG_MESSAGES.DELETE.DESCRIPTION(componentType), confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.DELETE })
    if (isConfirmed) {
      setIsDeleting(true)
      featureTour.delete(
        () => navigate('/admin/feature_tours'),
        () => { setIsDeleting(false); alert({ title: CONFIRMATION_DIALOG_MESSAGES.ERROR_MESSAGE.DELETE_FAILED, description: '' }) }
      )
    }
  }, [navigate, featureTour]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateFeatureCards = (selected: SiteMessageCard[], available: SiteMessageCard[]) => {
    const sortedSiteMessages = SiteMessageList(selected)
    const isUpdatedCards = sortedSiteMessages !== SiteMessageList(featureTour.siteMessages)

    setAvailableSiteMessages(available)
    setSelectedSiteMessages(sortedSiteMessages)
    setFeatureTourAttributes({ ...featureTourAttributes, ...{ siteMessages: sortedSiteMessages, isUpdatedCards } })
  }

  return (
    <>
      <Form
        displayPreviewButton
        previewButtonText="Preview Feature Tour"
        deleteButtonText="Feature Tour"
        isSaving={isSaving}
        isDeleting={isDeleting}
        displayDeleteButton={featureTourAttributes.id !== null}
        onSaveCallback={onSaveCallback}
        onCancelCallback={onCancelCallback}
        onDeleteCallback={onDeleteCallback}
        onPreviewCallback={() => setOpenPreview(!openPreview)}
      >
        <BrandFields
          selectedBrands={featureTourAttributes.brands}
          onChangeCallback={onFeatureTourUpdateCallback}
          errors={errors}
        />

        <RoleFields
          selectedRoles={featureTourAttributes.roles}
          onChangeCallback={onFeatureTourUpdateCallback}
          errors={errors}
        />

        <TierFields
          selectedTiers={featureTourAttributes.tiers}
          onChangeCallback={onFeatureTourUpdateCallback}
          errors={errors}
        />

        <FeatureTourFields
          internalName={featureTourAttributes.internalName}
          title={featureTourAttributes.title}
          message={featureTourAttributes.message}
          published={featureTourAttributes.published}
          publishedAt={featureTourAttributes.publishedAt || ''}
          expired={featureTourAttributes.expired}
          expiredAt={featureTourAttributes.expiredAt || ''}
          onChangeCallback={onFeatureTourUpdateCallback}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          errors={errors as any}
        />
        <br />
        <S.FormSection title="Feature Tour Cards*">
          <S.ListSelectAndSort
            subTitle="Add Site Messages to Feature Tour"
            selectedListLabel="Site Messages for Feature Tour"
            availableListLabel="Available Site Messages"
            initialSelected={selectedSiteMessages}
            initialAvailable={SiteMessageList(availableSiteMessages)}
            onChangeCallback={updateFeatureCards}
          />
        </S.FormSection>
        <div>
          <ErrorMessage errorMessage={errors?.siteMessages} />
        </div>

      </Form>
      <FeatureTourModal
        open={openPreview}
        title={featureTourAttributes.title}
        message={featureTourAttributes.message}
        siteMessages={selectedSiteMessages}
        closeCallback={() => setOpenPreview(!openPreview)}
      />
    </>
  )
}

export default FeatureTourForm

const S = {
  FormSection: styled(FormSection)`
    padding-top: 35px;
  `,
  Label: styled.label`
    font-weight: 700;
  `,
  ListSelectAndSort: styled(ListSelectAndSort)`
    display: flex;
    justify-content: space-between;
    margin-top: 5px;
    padding: 20px;
    border: 1px solid ${color('border')};
    border-radius: 5px;
    margin-top: 5px;
  `,
  CancelButton: styled(LegacyButton)`
    border-color:  ${color('cancelButtonBorder')};
    color:  ${color('cancelButtonText')};

    &:hover{
      border-color:  ${color('cancelButtonHoverBorder')};
      color:  ${color('cancelButtonHoverText')};
    }
  `
}
